import { useEffect, useRef } from 'react';
import { useIntersectionObserver } from '../../hooks/useIntersectionObserver';
import { useTypewriter } from '../../hooks/useTypewriter';

/**
 * Small sub-component that wraps the useTypewriter hook so each typewriter
 * instance gets its own hook call (hooks cannot be called in loops or
 * conditionally).
 */
function TypewriterText({
  text,
  delay = 0,
  enabled,
}: {
  text: string;
  delay?: number;
  enabled: boolean;
}) {
  const [currentText, isTyping] = useTypewriter({ text, delay, enabled });
  return <span className={isTyping ? 'typing' : ''}>{currentText}</span>;
}

/**
 * "Scout AI" section — ports the vanilla HTML scout section to React.
 *
 * Five scout feature cards plus a Gemini callout:
 *   1. Socratic Guidance — chat demo with typewriter text
 *   2. Graduated AI Withdrawal — 4 animated bars showing decreasing AI support
 *   3. Process-Based Assessment — comparison grid (others vs. ours)
 *   4. Reverse Turing Protocol — chat demo with typewriter text
 *   5. Hypothesis Lock — 3-step prediction demo
 *   6. Powered by Google Gemini callout
 *
 * A single IntersectionObserver on the <section> drives all fade-up reveals.
 * Withdrawal bars animate with a 200ms stagger when the section becomes visible.
 */
export function ScoutSection() {
  const [sectionRef, isVisible] = useIntersectionObserver<HTMLElement>({ threshold: 0.1 });

  const reveal = () => `fade-up ${isVisible ? 'revealed' : ''}`;

  const stagger = (i: number): React.CSSProperties =>
    ({ '--stagger': i }) as React.CSSProperties;

  /* ---- Withdrawal bar animation ---- */
  const withdrawalRef = useRef<HTMLDivElement>(null);
  const hasAnimatedBars = useRef(false);

  useEffect(() => {
    if (!isVisible || hasAnimatedBars.current) return;
    hasAnimatedBars.current = true;
    const fills = withdrawalRef.current?.querySelectorAll('.withdrawal-fill');
    fills?.forEach((fill, i) => {
      setTimeout(() => fill.classList.add('animated'), i * 200);
    });
  }, [isVisible]);

  return (
    <section
      id="scout"
      className="section scout-section"
      aria-label="Scout AI"
      ref={sectionRef}
    >
      <div className="section-inner">
        <h2 className={`section-title ${reveal()}`}>Scout AI</h2>
        <p className={`section-subtitle ${reveal()}`} style={stagger(1)}>
          The AI tutor that makes itself unnecessary.
        </p>

        <div className="scout-features" data-stagger>
          {/* ---- 1. Socratic Guidance ---- */}
          <div className={`scout-card ${reveal()}`}>
            <md-elevation />
            <div className="scout-card-header">
              <span className="material-symbols-outlined scout-card-icon">forum</span>
              <h3>Socratic Guidance</h3>
            </div>
            <p className="scout-card-desc">
              Scout never gives the answer. It asks the right question at the right time to guide
              learners to their own Level&nbsp;3 thinking: true, transferable understanding.
            </p>
            <div className="scout-chat-demo">
              <div className="chat-bubble chat-student">
                Can you just give me the answer? I&apos;m stuck on this recursive function.
              </div>
              <div className="chat-bubble chat-scout">
                <TypewriterText
                  text="I could, but then you'd have Level 1 knowledge, not Level 3. Let's build real understanding. What value does your function return when it reaches the base case?"
                  delay={0}
                  enabled={isVisible}
                />
              </div>
              <div className="chat-bubble chat-student">
                Oh! I forgot the return statement in the base case!
              </div>
              <div className="chat-bubble chat-scout">
                <TypewriterText
                  text="Now you own that knowledge. You'll never make that mistake again. That's the difference between recognizing and applying."
                  delay={2000}
                  enabled={isVisible}
                />
              </div>
            </div>
          </div>

          {/* ---- 2. Graduated AI Withdrawal ---- */}
          <div className={`scout-card ${reveal()}`}>
            <md-elevation />
            <div className="scout-card-header">
              <span className="material-symbols-outlined scout-card-icon">trending_down</span>
              <h3>Graduated AI Withdrawal</h3>
            </div>
            <p className="scout-card-desc">
              AI support deliberately decreases as competence increases. The goal isn&apos;t AI
              dependency; it&apos;s learner independence.
            </p>
            <div className="ai-withdrawal-bars" ref={withdrawalRef}>
              <div className="withdrawal-bar" data-week="1-4" data-level="100">
                <span className="withdrawal-label">Week 1–4</span>
                <div className="withdrawal-track">
                  <div
                    className="withdrawal-fill"
                    style={{ '--withdrawal-target': '100%' } as React.CSSProperties}
                  />
                </div>
                <span className="withdrawal-desc">Full scaffolding</span>
              </div>
              <div className="withdrawal-bar" data-week="5-8" data-level="65">
                <span className="withdrawal-label">Week 5–8</span>
                <div className="withdrawal-track">
                  <div
                    className="withdrawal-fill"
                    style={{ '--withdrawal-target': '65%' } as React.CSSProperties}
                  />
                </div>
                <span className="withdrawal-desc">Guided independence</span>
              </div>
              <div className="withdrawal-bar" data-week="9-12" data-level="35">
                <span className="withdrawal-label">Week 9–12</span>
                <div className="withdrawal-track">
                  <div
                    className="withdrawal-fill"
                    style={{ '--withdrawal-target': '35%' } as React.CSSProperties}
                  />
                </div>
                <span className="withdrawal-desc">Selective intervention</span>
              </div>
              <div className="withdrawal-bar" data-week="13-16" data-level="10">
                <span className="withdrawal-label">Week 13–16</span>
                <div className="withdrawal-track">
                  <div
                    className="withdrawal-fill"
                    style={{ '--withdrawal-target': '10%' } as React.CSSProperties}
                  />
                </div>
                <span className="withdrawal-desc">Autonomous mastery</span>
              </div>
            </div>
          </div>

          {/* ---- 3. Process-Based Assessment ---- */}
          <div className={`scout-card ${reveal()}`}>
            <md-elevation />
            <div className="scout-card-header">
              <span className="material-symbols-outlined scout-card-icon">query_stats</span>
              <h3>Process-Based Assessment</h3>
            </div>
            <p className="scout-card-desc">
              We don&apos;t just measure what you know; we measure how you think. That&apos;s where
              the real learning signal lives.
            </p>
            <div className="process-assessment">
              <div className="assessment-column assessment-others">
                <h4>What others measure</h4>
                <ul>
                  <li>Correct answer</li>
                  <li>Time to complete</li>
                  <li>Completion rate</li>
                </ul>
              </div>
              <div className="assessment-divider">
                <span className="material-symbols-outlined">compare_arrows</span>
              </div>
              <div className="assessment-column assessment-ours">
                <h4>What we measure</h4>
                <ul>
                  <li>Reasoning path</li>
                  <li>Error patterns</li>
                  <li>Strategy shifts</li>
                  <li>Transfer ability</li>
                  <li>Metacognitive signals</li>
                </ul>
              </div>
            </div>
          </div>

          {/* ---- 4. Reverse Turing Protocol ---- */}
          <div className={`scout-card ${reveal()}`}>
            <md-elevation />
            <div className="scout-card-header">
              <span className="material-symbols-outlined scout-card-icon">bug_report</span>
              <h3>Reverse Turing Protocol</h3>
            </div>
            <p className="scout-card-desc">
              Scout deliberately presents flawed mental models for learners to debug. Finding errors
              in AI reasoning is harder than generating answers and builds far deeper understanding.
            </p>
            <div className="scout-chat-demo">
              <div className="chat-bubble chat-scout">
                <TypewriterText
                  text="Here's my understanding: a recursive function calls itself until the stack overflows, then JavaScript catches the error and returns undefined. Is that right?"
                  delay={0}
                  enabled={isVisible}
                />
              </div>
              <div className="chat-bubble chat-student">
                No! Recursion needs a base case to stop. Without one you get infinite calls, but JS
                throws a RangeError; it doesn&apos;t return undefined.
              </div>
              <div className="chat-bubble chat-scout">
                <TypewriterText
                  text="Exactly. You just demonstrated Level 3 thinking. You didn't just recognize the error, you corrected the mental model. That's ownership."
                  delay={2000}
                  enabled={isVisible}
                />
              </div>
            </div>
          </div>

          {/* ---- 5. Hypothesis Lock ---- */}
          <div className={`scout-card ${reveal()}`}>
            <md-elevation />
            <div className="scout-card-header">
              <span className="material-symbols-outlined scout-card-icon">lock</span>
              <h3>Hypothesis Lock</h3>
            </div>
            <p className="scout-card-desc">
              Before running any code, learners must commit a prediction. This locks in a mental
              model, creates a prediction-error signal when wrong, and accelerates memory
              consolidation.
            </p>
            <div className="hypothesis-lock-demo">
              <div className="lock-step">
                <span className="lock-step-num">1</span>
                <div className="lock-step-content">
                  <strong>Predict</strong>
                  <span>
                    What will <code>[1,2,3].map(String)</code> return?
                  </span>
                  <div className="lock-input-mock">
                    <span className="lock-input-text">[&quot;1&quot;, &quot;2&quot;, &quot;3&quot;]</span>
                    <span className="material-symbols-outlined lock-icon">lock</span>
                  </div>
                </div>
              </div>
              <div className="lock-step">
                <span className="lock-step-num">2</span>
                <div className="lock-step-content">
                  <strong>Execute</strong>
                  <span>Run the code and compare.</span>
                </div>
              </div>
              <div className="lock-step">
                <span className="lock-step-num">3</span>
                <div className="lock-step-content">
                  <strong>Reflect</strong>
                  <span>
                    Was your mental model correct? If not, what assumption failed?
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ---- Powered by Google Gemini callout ---- */}
        <div className={`gemini-callout ${reveal()}`}>
          <md-elevation />
          <div className="gemini-callout-header">
            <span className="material-symbols-outlined">auto_awesome</span>
            <h3>Powered by Google Gemini</h3>
          </div>
          <div className="gemini-points">
            <div className="gemini-point">
              <strong>1M Token Context Window</strong>
              <p>
                Understands entire 20K–50K line codebases. Scout sees the full picture, not just the
                current file.
              </p>
            </div>
            <div className="gemini-point">
              <strong>Advanced Multi-Step Reasoning</strong>
              <p>
                Multi-turn Socratic conversations that adapt to each learner&apos;s mental model in
                real time.
              </p>
            </div>
            <div className="gemini-point">
              <strong>Multimodal Future</strong>
              <p>
                Voice tutoring, visual debugging, and gesture recognition: the next frontier of
                AI-powered education.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
